'use client';

import React, { useCallback, useRef } from 'react';

import { AnimatePresence, motion, useMotionValue } from 'framer-motion';

import useBodyScrollLock from '@/hooks/use-body-scroll-lock';

interface BottomSheetProps {
  isOpen: boolean;
  bgColor?: string;
  /** 최대 높이 (예: '80dvh', '90%') */
  maxHeight?: string;
  /** 높이를 고정하고 싶을 때 사용 (예: '90dvh', 'calc(100dvh - 30px)') */
  fixHeight?: string;
  onClose: () => void;
  /** 드래그 핸들 + topContent 대신 사용할 커스텀 헤더 (예: 노란 배경 타이틀 바) */
  headerContent?: React.ReactNode;
  topContent?: React.ReactNode;
  children: React.ReactNode;
  hideHeader?: boolean;
  hideHandleMargin?: boolean;
}

/** MainSectionLayout 우측 패널과 동일한 위치(460+140), 최대 500px 고정 */
const RIGHT_PANEL_LEFT =
  'calc((100vw - 600px - min(500px, 100vw - 600px)) / 2 + 600px)';
const RIGHT_PANEL_WIDTH = 'min(500px, calc(100vw - 600px))';

const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  bgColor = '#EFEEF7',
  maxHeight,
  fixHeight,
  onClose,
  headerContent,
  topContent,
  children,
  hideHeader = false,
  hideHandleMargin = false,
}) => {
  useBodyScrollLock(isOpen);
  const y = useMotionValue(0);
  const sheetRef = useRef<HTMLDivElement>(null);
  const effectiveMaxHeight = maxHeight ?? fixHeight ?? '80dvh';

  const handleDragEnd = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: { offset: { y: number }; velocity: { y: number } },
  ) => {
    if (info.offset.y > 30 || info.velocity.y > 300) {
      onClose();
    } else {
      y.set(0);
    }
  };

  const closeBottomSheet = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 좁은 화면: 전체 화면 오버레이. 넓은 화면(1025px+): 우측 패널에만 오버레이 */}
          <motion.div
            className="fixed inset-0 z-[60] bg-black/50 min-[1025px]:left-[var(--bottom-sheet-panel-left)] min-[1025px]:inset-x-auto min-[1025px]:w-[var(--bottom-sheet-panel-width)] min-[1025px]:max-w-[500px]"
            style={
              {
                '--bottom-sheet-panel-left': RIGHT_PANEL_LEFT,
                '--bottom-sheet-panel-width': RIGHT_PANEL_WIDTH,
              } as React.CSSProperties
            }
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeBottomSheet}
          />

          {/* 넓은 화면: 좌측 영역 클릭 시에도 닫기 (오버레이 밖) */}
          <div
            className="fixed inset-0 z-[50] hidden min-[1025px]:block"
            style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
            onClick={closeBottomSheet}
            aria-hidden
          />

          <motion.div
            ref={sheetRef}
            className="fixed inset-x-0 bottom-0 z-[70] left-0 right-0 mobile-w mx-auto flex w-full flex-col rounded-t-2xl min-[1025px]:left-[var(--bottom-sheet-panel-left)] min-[1025px]:right-auto min-[1025px]:mx-0 min-[1025px]:w-[var(--bottom-sheet-panel-width)] min-[1025px]:max-w-[500px]"
            style={{
              y,
              backgroundColor: bgColor,
              maxHeight: effectiveMaxHeight,
              height: fixHeight,
              ['--bottom-sheet-panel-left' as string]: RIGHT_PANEL_LEFT,
              ['--bottom-sheet-panel-width' as string]: RIGHT_PANEL_WIDTH,
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'tween', damping: 25, stiffness: 500 }}
          >
            {!hideHeader && (
              <>
                {headerContent ? (
                  /* 커스텀 헤더 (드래그로 닫기 지원) */
                  <motion.div
                    drag="y"
                    dragConstraints={{ top: 0, bottom: 0 }}
                    dragElastic={0}
                    onDragEnd={handleDragEnd}
                    className="cursor-grab shrink-0 active:cursor-grabbing"
                  >
                    {headerContent}
                  </motion.div>
                ) : (
                  <>
                    {/* 드래그 핸들 */}
                    <motion.div
                      drag="y"
                      style={{ y: 0 }}
                      whileDrag={{ y: 0 }}
                      dragConstraints={{ top: 0, bottom: 0 }}
                      dragElastic={0.05}
                      onDragEnd={handleDragEnd}
                      className="cursor-grab p-4 active:cursor-grabbing"
                    >
                      <div
                        className={`mx-auto h-1 w-[38px] rounded-full bg-[#D6D4E2] ${!hideHandleMargin ? 'mb-4' : ''}`}
                      />
                    </motion.div>

                    {/* 옵션 상단 콘텐츠 */}
                    {topContent && (
                      <motion.div
                        drag="y"
                        dragConstraints={{ top: 0, bottom: 0 }}
                        dragElastic={0.05}
                        onDragEnd={handleDragEnd}
                        className="cursor-grab p-4 active:cursor-grabbing"
                      >
                        {topContent}
                      </motion.div>
                    )}
                  </>
                )}
              </>
            )}
            {/* 내부 스크롤 가능한 메인 콘텐츠 영역 */}
            <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BottomSheet;
